import {
	FormData,
	FormField,
	FormFieldDataType,
	FormFieldGroup,
	FormFieldGroupSettings,
	FormFieldListener,
	FormFieldOption,
	FormFieldTranslation,
	FormFieldTypeKey,
	FormPlugin,
	FormSetting,
	FormTranslation,
	FormWizardStep,
	LegacyApiForm,
	LegacyApiFormField,
	LegacyApiFormFieldGroup,
	LegacyApiFormFieldOption,
	LegacyApiFormPlugin,
	LegacyApiFormSetting,
	LegacyApiFormTranslation,
	LegacyApiFormWizardStep,
} from './forms';

// ---------------------------------------------------------------------------
// Main normalizer
// ---------------------------------------------------------------------------

/**
 * Converts a legacy PascalCase form API response into the clean camelCase FormData contract.
 */
export function normalizeFormData(legacy: LegacyApiForm): FormData {
	const fields = (legacy.Fields ?? []).sort((a, b) => a.Order - b.Order).map(normalizeField);

	return {
		id: legacy.Id,
		name: legacy.Name ?? '',
		nameTranslations: (legacy.NameTranslations ?? []).map(normalizeTranslation),
		containerModuleGuid: legacy.ContainerModuleGuid ?? '',
		createdDateUtc: legacy.CreatedDateUtc ?? null,
		publishDateUtc: legacy.PublishDateUtc ?? null,
		expiresDateUtc: legacy.ExpiresDateUtc ?? null,
		updatedDateUtc: legacy.UpdatedDateUtc ?? null,
		fields,
		fieldGroups: (legacy.FieldGroups ?? []).map(normalizeFieldGroup),
		wizardSteps: (legacy.WizardSteps ?? []).map(normalizeWizardStep),
		settings: (legacy.Settings ?? []).map(normalizeSetting),
		plugin: legacy.Plugin ? normalizePlugin(legacy.Plugin) : null,
		pluginAssemblyName: legacy.PluginAssemblyName ?? null,
		pluginClassName: legacy.PluginClassName ?? null,
		pluginSettings: legacy.PluginSettings ?? null,
		emailRecipients: legacy.EmailRecipients ?? null,
		sendConfirmEmail: legacy.SendConfirmEmail ?? false,
		submissionCount: legacy.SubmissionCount ?? 0,
		currentUserSubmissionCount: legacy.CurrentUserSubmissionCount ?? 0,
		giveXp: legacy.GiveXp ?? false,
		giveVirtualCurrency: legacy.GiveVirtualCurrency ?? false,
		missingSubmissionDatesUtc: legacy.MissingSubmissionDatesUtc,
	};
}

// ---------------------------------------------------------------------------
// Field normalizer
// ---------------------------------------------------------------------------

function normalizeField(legacy: LegacyApiFormField): FormField {
	const customSettings = parseCustomSettings(legacy.CustomSettings);
	const templateOptions: Record<string, unknown> = customSettings.TemplateOptions ?? {};
	const data: Record<string, unknown> = customSettings.Data ?? {};

	// Merge NameTranslations (type 1) and PlaceholderTranslations (type 2) into one array
	const translations: FormFieldTranslation[] = [
		...(legacy.NameTranslations ?? []).map((t) => normalizeFieldTranslation(t, legacy.Key)),
		...(legacy.PlaceholderTranslations ?? []).map((t) => normalizeFieldTranslation(t, legacy.Key)),
	];

	const rawOptions = legacy.Options ?? (data.options as LegacyApiFormFieldOption[] | undefined) ?? [];
	const rawListeners = (data.listeners as Record<string, unknown>[] | undefined) ?? [];

	return {
		id: legacy.Id,
		key: legacy.Key,
		typeKey: (legacy.Type ?? data.typeKey ?? 'input') as FormFieldTypeKey,
		label: legacy.Name ?? (templateOptions.label as string) ?? '',
		placeholder: legacy.Placeholder ?? (templateOptions.placeholder as string) ?? '',
		required: legacy.IsMandatory ?? (templateOptions.required as boolean) ?? false,
		order: legacy.Order ?? (templateOptions.order as number) ?? 0,
		widthClass: legacy.Width ?? '',
		dataType: (data.dataType as FormFieldDataType) ?? null,
		groupKey: legacy.FormFieldGroupKey ?? (data.groupKey as string) ?? '',
		wizardStepKey: legacy.FormWizardStepKey ?? (data.wizardStepKey as string) ?? '',
		translations,
		options: rawOptions.map(normalizeFieldOption),
		listeners: rawListeners.map(normalizeFieldListener),
		settings: extractFieldSettings(templateOptions),
	};
}

/**
 * Extracts type-specific settings from the legacy templateOptions and data objects.
 * These are settings like input type validation, slider min/max/step, checkbox description, etc.
 */
function extractFieldSettings(templateOptions: Record<string, unknown>): Record<string, unknown> {
	const settings: Record<string, unknown> = {};

	// Input type validation (email, password, number)
	if (templateOptions.type !== undefined) settings.type = templateOptions.type;

	// Slider settings
	if (templateOptions.min !== undefined) settings.min = templateOptions.min;
	if (templateOptions.max !== undefined) settings.max = templateOptions.max;
	if (templateOptions.step !== undefined) settings.step = templateOptions.step;
	if (templateOptions.unit !== undefined) settings.unit = templateOptions.unit;

	// Checkbox description
	if (templateOptions.description !== undefined) settings.description = templateOptions.description;
	if (templateOptions.infoicon !== undefined) settings.infoicon = templateOptions.infoicon;

	// List alignment
	if (templateOptions.optionsinrow !== undefined) settings.optionsinrow = templateOptions.optionsinrow;

	// Checkbox list allowed selections
	if (templateOptions.allowedselections !== undefined) settings.allowedselections = templateOptions.allowedselections;

	// Dropdown placeholder
	const noSelectText = getFirstDefined(templateOptions, ['noselecttext', 'noSelectText', 'NoSelectText']);
	if (noSelectText !== undefined) settings.noselecttext = noSelectText;

	// Video setting
	if (templateOptions.video !== undefined) settings.video = templateOptions.video;

	// Culture-dependent options
	if (templateOptions.CultureOptions !== undefined) settings.cultureOptions = templateOptions.CultureOptions;

	return settings;
}

// ---------------------------------------------------------------------------
// Sub-entity normalizers
// ---------------------------------------------------------------------------

function normalizeTranslation(legacy: LegacyApiFormTranslation): FormTranslation {
	return {
		cultureId: legacy.CultureId,
		type: legacy.Type,
		value: legacy.Value ?? '',
	};
}

function normalizeFieldTranslation(legacy: LegacyApiFormTranslation, fieldKey: string): FormFieldTranslation {
	return {
		fieldKey: legacy.FieldKey ?? fieldKey,
		cultureId: legacy.CultureId,
		type: legacy.Type,
		value: legacy.Value ?? '',
	};
}

function normalizeFieldOption(legacy: LegacyApiFormFieldOption): FormFieldOption {
	return {
		id: String(legacy.Id),
		formFieldId: String(legacy.FormFieldId),
		value: legacy.Value ?? 0,
		translations: (legacy.Translations ?? []).map(normalizeTranslation),
		settings: {
			preselected: legacy.Settings?.Preselected === true,
			disabled: legacy.Settings?.Disabled === true,
		},
	};
}

function normalizeFieldListener(legacy: Record<string, unknown>): FormFieldListener {
	return {
		key: (legacy.Key as string) ?? '',
		value: (legacy.Value as string) ?? '',
		action: ((legacy.Action as string) ?? 'show') as 'show' | 'hide',
	};
}

function normalizeFieldGroup(legacy: LegacyApiFormFieldGroup): FormFieldGroup {
	const settings = parseJsonSettings(legacy.Settings);

	return {
		key: legacy.Key,
		name: legacy.Name ?? '',
		formId: legacy.FormId ?? 0,
		translations: (legacy.Translations ?? []).map(normalizeTranslation),
		order: legacy.Order ?? 0,
		settings: normalizeGroupSettings(settings),
	};
}

function normalizeGroupSettings(raw: Record<string, unknown>): FormFieldGroupSettings {
	return {
		expandable: raw.Expandable === true || raw.expandable === true,
		startExpanded: raw.StartExpanded === true || raw.startExpanded === true,
		borderWidth: (raw.BorderWidth ?? raw.borderWidth) as string | undefined,
		borderColor: (raw.BorderColor ?? raw.borderColor) as string | undefined,
		headerBackgroundColor: (raw.HeaderBackgroundColor ?? raw.headerBackgroundColor) as string | undefined,
		backgroundColor: (raw.BackgroundColor ?? raw.backgroundColor) as string | undefined,
		headerTextColor: (raw.HeaderTextColor ?? raw.headerTextColor) as string | undefined,
		textColor: (raw.TextColor ?? raw.textColor) as string | undefined,
		imageUrl: extractImageUrl(raw.Image ?? raw.image),
		wizardStepKey: (raw.FormWizardStepKey ?? raw.wizardStepKey) as string | undefined,
	};
}

function normalizeWizardStep(legacy: LegacyApiFormWizardStep): FormWizardStep {
	return {
		key: legacy.Key,
		name: legacy.Name ?? null,
		formId: legacy.FormId ?? 0,
		translations: (legacy.Translations ?? []).map(normalizeTranslation),
		order: legacy.Order ?? 0,
		settings: parseJsonSettings(legacy.Settings),
	};
}

function normalizeSetting(legacy: LegacyApiFormSetting): FormSetting {
	return {
		key: legacy.Key,
		value: legacy.Value ?? '',
	};
}

function normalizePlugin(legacy: LegacyApiFormPlugin): FormPlugin {
	return {
		key: legacy.Key,
		name: legacy.Name ?? '',
		assemblyName: legacy.AssemblyName ?? null,
		className: legacy.ClassName ?? null,
		fieldName: legacy.FieldName ?? null,
		fieldTypes: legacy.FieldTypes ?? [],
		pluginJavascriptSrc: legacy.PluginJavascriptSrc ?? null,
	};
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function parseCustomSettings(raw: string | null | undefined): Record<string, Record<string, unknown>> {
	if (!raw) return {};
	try {
		const parsed: unknown = JSON.parse(raw);
		if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as Record<string, Record<string, unknown>>;
		}
		return {};
	} catch {
		return {};
	}
}

function getFirstDefined(raw: Record<string, unknown>, keys: string[]): unknown {
	for (const key of keys) {
		if (raw[key] !== undefined) {
			return raw[key];
		}
	}

	return undefined;
}

function parseJsonSettings(raw: string | null | undefined): Record<string, unknown> {
	if (!raw) return {};
	try {
		const parsed: unknown = JSON.parse(raw);
		if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as Record<string, unknown>;
		}
		return {};
	} catch {
		return {};
	}
}

/**
 * Extracts a file URL from an image object or string.
 * Legacy stores images as `{ FileUrl: '...' }` or plain strings.
 */
function extractImageUrl(raw: unknown): string | undefined {
	if (typeof raw === 'string' && raw) return raw;
	if (raw && typeof raw === 'object') {
		const obj = raw as Record<string, unknown>;
		const url = obj.FileUrl ?? obj.fileUrl;
		if (typeof url === 'string' && url) return url;
	}
	return undefined;
}
